// Port of SuiteModel/JoinedMap.swift: every map object stored at one key, read as one map.
//
// A map that is made on first use — `settings.glossary`, a fragment's `wordLog`, the Trash arrangement — is made by
// each of two devices that use it before they sync. Automerge keeps both objects at the key as a conflict and shows
// one, so what the other device put in its own is in the document and never seen. Documents in the field hold such
// pairs, so they are read rather than avoided: a reader takes the union of every map at the key (and of the maps
// inside them, key by key), a writer adds to the first of them (`Writer`, edit/typed.ts), and a delete takes the key
// out of each.
//
// The order is the Swift's, so both read a key that several maps hold from the same one: the map Automerge shows,
// then the others newest first by the op that made each (its counter, then its actor: how Automerge itself picks the
// one to show).
import { ROOT, type ObjID, type SwiftReader } from './swiftDoc.ts';

type Full = [string, unknown] | null;

/** An object id's op, "12@abcd…": its counter and its actor. */
function opOf(id: ObjID): [number, string] {
  const at = id.indexOf('@');
  return at < 0 ? [0, id] : [Number(id.slice(0, at)), id.slice(at + 1)];
}

/** `AutomergeActor.isNewer`: ops as Automerge orders them (by counter, then by actor), the later first. */
export function newerFirst(a: ObjID, b: ObjID): number {
  const [x, p] = opOf(a), [y, q] = opOf(b);
  return x !== y ? y - x : p < q ? 1 : p > q ? -1 : 0;
}

/** `Document.objects`: every object of `type` stored at `key`, conflicting values included, in `JoinedMap`'s order. */
export function objectsAt(r: SwiftReader, obj: ObjID, key: string, type: 'map' | 'list' | 'text'): ObjID[] {
  const all = (r.backend.getAll(obj, key, r.heads) as unknown as [string, unknown, string][] | undefined) ?? [];
  const ids = all.filter((v) => v[0] === type).map((v) => v[1] as ObjID);
  if (ids.length <= 1) return ids;
  ids.sort(newerFirst);
  const shown = r.get(obj, key);
  const at = shown && shown[0] === type ? ids.indexOf(shown[1] as ObjID) : -1;
  if (at > 0) ids.unshift(...ids.splice(at, 1));
  return ids;
}

/** `JoinedMap`. */
export class JoinedMap {
  constructor(
    readonly r: SwiftReader,
    /** The maps, in reading order. Empty when there is no map at the key. */
    readonly maps: ObjID[],
  ) {}

  get isEmpty(): boolean {
    return this.maps.length === 0;
  }

  /** Every key any of the maps holds, each once, in the maps' order. */
  keys(): string[] {
    if (this.maps.length === 1) return this.r.keys(this.maps[0]);
    const seen = new Set<string>();
    for (const map of this.maps) for (const key of this.r.keys(map)) seen.add(key);
    return [...seen];
  }

  /** The first of the maps that holds `key`. */
  holder(key: string): ObjID | undefined {
    return this.maps.find((map) => this.r.get(map, key) !== null);
  }

  /** The value at `key` ([datatype, value or object id]), from the first map that holds it. */
  get(key: string): Full {
    for (const map of this.maps) {
      const v = this.r.get(map, key);
      if (v) return v;
    }
    return null;
  }

  entries(): [string, [string, unknown]][] {
    return this.keys().flatMap((k) => {
      const v = this.get(k);
      return v ? [[k, v] as [string, [string, unknown]]] : [];
    });
  }

  /** Every map at `key` in any of these maps, as one. */
  joined(key: string): JoinedMap {
    return new JoinedMap(this.r, this.maps.flatMap((map) => objectsAt(this.r, map, key, 'map')));
  }

  /** The map at `key` to read an entry's fields from: the one `get` gives. */
  map(key: string): ObjID | undefined {
    const v = this.get(key);
    return v && v[0] === 'map' ? (v[1] as ObjID) : undefined;
  }

  private read<T>(key: string, body: (obj: ObjID) => T | undefined): T | undefined {
    if (this.maps.length === 1) return body(this.maps[0]);
    const holder = this.holder(key);
    return holder === undefined ? undefined : body(holder);
  }

  string(key: string): string | undefined { return this.read(key, (o) => this.r.string(o, key)); }
  int(key: string): number | undefined { return this.read(key, (o) => this.r.int(o, key)); }
  bool(key: string): boolean | undefined { return this.read(key, (o) => this.r.bool(o, key)); }
  double(key: string): number | undefined { return this.read(key, (o) => this.r.double(o, key)); }
  date(key: string): Date | undefined { return this.read(key, (o) => this.r.date(o, key)); }
}

/** `Document.joined`: every map at `key`, as one; empty when there is none. */
export function joinedAt(r: SwiftReader, obj: ObjID, key: string): JoinedMap {
  return new JoinedMap(r, objectsAt(r, obj, key, 'map'));
}

/** The maps at a path of map keys from the root, joined at every step (`settings` → `glossary`). */
export function joinedPath(r: SwiftReader, path: readonly string[]): JoinedMap {
  let at = new JoinedMap(r, [ROOT]);
  for (const key of path) at = at.joined(key);
  return at;
}
